import { useEffect, useMemo, useState } from "react";
import { Card, EmptyState, PageHeader, useEntityList } from "@pericles/framework-frontend";
import { useLocation, useNavigate } from "react-router-dom";

import { ProjectMeetingsTab } from "./ProjectMeetingsTab";
import { Button } from "./ui";

type ProjectOption = {
  id: string | number;
  name?: string;
  code?: string;
};

export function ForumScreen(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const requestedProjectId = searchParams.get("projectId") ?? "";
  const [selectedProjectId, setSelectedProjectId] = useState<string>(requestedProjectId);

  const { data: projectsData, isLoading: isLoadingProjects } = useEntityList<ProjectOption>({
    entity: "Project",
    page: 0,
    pageSize: 200,
  });

  const projects = useMemo(
    () =>
      (projectsData ?? []).map((project) => ({
        value: String(project.id),
        label: project.name ? `${project.name} (${project.code ?? project.id})` : String(project.id),
      })),
    [projectsData],
  );

  useEffect(() => {
    setSelectedProjectId(requestedProjectId);
  }, [requestedProjectId]);

  useEffect(() => {
    if (selectedProjectId || !projects.length) {
      return;
    }

    const firstProjectId = projects[0]?.value ?? "";
    if (!firstProjectId) {
      return;
    }

    setSelectedProjectId(firstProjectId);
    const nextParams = new URLSearchParams(location.search);
    nextParams.set("projectId", firstProjectId);
    navigate(`/forum?${nextParams.toString()}`, { replace: true });
  }, [location.search, navigate, projects, selectedProjectId]);

  const handleProjectChange = (projectId: string) => {
    setSelectedProjectId(projectId);
    const nextParams = new URLSearchParams(location.search);
    if (projectId) {
      nextParams.set("projectId", projectId);
    } else {
      nextParams.delete("projectId");
    }
    const query = nextParams.toString();
    navigate(`/forum${query ? `?${query}` : ""}`, { replace: true });
  };

  return (
    <div className="content-stack">
      <PageHeader
        title="Forum"
        subtitle="Meeting intelligence workspace with recording, diarization, live coaching, and debrief analysis."
      />

      <Card title="Project selection">
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-sm font-semibold text-slate-800" htmlFor="forum-project">
            Project
          </label>
          <select
            id="forum-project"
            className="input min-w-[320px]"
            value={selectedProjectId}
            onChange={(event) => handleProjectChange(event.target.value)}
            disabled={isLoadingProjects}
          >
            {!selectedProjectId ? <option value="">Select a project</option> : null}
            {projects.map((project) => (
              <option key={project.value} value={project.value}>
                {project.label}
              </option>
            ))}
          </select>
          {selectedProjectId ? (
            <Button type="button" variant="secondary" onClick={() => navigate(`/projects/${selectedProjectId}?tab=meetings`)}>
              Open in project
            </Button>
          ) : null}
        </div>
      </Card>

      {selectedProjectId ? (
        <ProjectMeetingsTab projectId={selectedProjectId} showHeader={false} />
      ) : (
        <EmptyState
          title="No project selected"
          description="Pick a project to open the meeting capture and coaching workspace."
        />
      )}
    </div>
  );
}
